import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Drawer, Button, Space, Alert, message, Spin, Typography, Card, Switch, Select, Divider, Tag, Row, Col } from "antd";
import { MailOutlined, ThunderboltOutlined, FileTextOutlined, CoffeeOutlined, FileProtectOutlined, CarOutlined, HomeOutlined, SendOutlined } from "@ant-design/icons";
import { emailTemplateApi } from "../../../../api/emailTemplateApi";
import { sendUniversalEmail } from "../../../../api/flightOperationEmailApi";
import { getPresetSenderAccounts } from "../../../../api/mailApi";
import { flightOperationFlightService } from "../../../../api/enhancedApi";
import { useAuth } from "../../../../hooks/useAuth";
import useAirports from "../../../../hooks/useAirports";
import GeneralEmailEditor from "../../../../components/EmailEditor/GeneralEmailEditor";
import AircraftTag from "../../../../components/AircraftTag";
import { adaptUserEmailConfig } from "../../../../components/EmailEditor/components/EmailEditorForm";
import { EmailType, getInitialOptionValues, SendingOptionType } from "../../../../config/emailTypeConfig";
import { TRANSACTION_TYPES, TRANSACTION_TYPE_LABELS } from "../constants";

const { Text, Title } = Typography;
const { Option } = Select;

/**
 * 执行航班事务统一邮件发送抽屉组件
 * 支持各种事务类型的邮件发送
 *
 * @param {boolean} visible - 抽屉是否可见
 * @param {function} onClose - 关闭抽屉的回调
 * @param {Array} selectedFlightIds - 选中的航班ID列表
 * @param {Array} allFlightsData - 包含所有航班详细信息的数组
 * @param {string} transactionType - 事务类型
 * @param {Array} selectedContractItems - 选中的合同项列表（用于提取联系人邮箱）
 * @param {function} onSuccess - 发送成功的回调
 */
const UnifiedTransactionEmailDrawer = ({
    visible,
    onClose,
    selectedFlightIds = [],
    allFlightsData = [],
    transactionType,
    selectedContractItems = [], // 保留以维持向后兼容
    onSuccess,
    initialToRecipients = [],
    initialCcRecipients = [],
    // 新增：从批量事务上下文传递的信息
    contextAirports = null, // 当前选择的机场列表
    contextSupplier = null, // 当前选择的供应商
}) => {
    const { userEmailConfig } = useAuth();
    const { airportsMap } = useAirports();
    // 基础状态
    const [loading, setLoading] = useState(false);
    const [selectedFlightDetails, setSelectedFlightDetails] = useState([]);
    const [extendedFlightData, setExtendedFlightData] = useState([]); // 包含前序航班的扩展数据
    const [isEmailEditorVisible, setIsEmailEditorVisible] = useState(false);
    const [isEmailLoading, setIsEmailLoading] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [sendingProgress, setSendingProgress] = useState(null);

    // 模板相关状态
    const [templateLoading, setTemplateLoading] = useState(false);
    const [allTemplates, setAllTemplates] = useState({});
    const [selectedTemplateCode, setSelectedTemplateCode] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [isTemplateInitialized, setIsTemplateInitialized] = useState(false);
    const [availableTemplates, setAvailableTemplates] = useState([]);

    // 发件人相关状态
    const [senderOptions, setSenderOptions] = useState([]);

    // 发送选项状态 - 使用新的emailTypeConfig API
    const initialSendingOptions = getInitialOptionValues(EmailType.GENERAL);
    const [sendingOptions, setSendingOptions] = useState(initialSendingOptions);

    // 根据事务类型获取图标
    const getTransactionIcon = (type) => {
        const iconMap = {
            [TRANSACTION_TYPES.FUEL_REQUEST]: <ThunderboltOutlined style={{ color: "#fa8c16" }} />,
            [TRANSACTION_TYPES.PERMIT_REQUEST]: <FileProtectOutlined style={{ color: "#1890ff" }} />,
            [TRANSACTION_TYPES.FLIGHT_BRIEF]: <FileTextOutlined style={{ color: "#52c41a" }} />,
            [TRANSACTION_TYPES.CATERING_ORDER]: <CoffeeOutlined style={{ color: "#eb2f96" }} />,
            [TRANSACTION_TYPES.CREW_TRANSPORTATION]: <CarOutlined style={{ color: "#722ed1" }} />,
            [TRANSACTION_TYPES.HOTAC]: <HomeOutlined style={{ color: "#13c2c2" }} />,
        };
        return iconMap[type] || <MailOutlined style={{ color: "#666" }} />;
    };

    // 获取当前上下文的机场和供应商信息
    const getCurrentContextInfo = useCallback(() => {
        const airports = new Set();
        const supplierIds = new Set();

        // 只使用从批量事务上下文明确传递的信息，不做回退
        if (contextAirports && contextAirports.length > 0) {
            contextAirports.forEach((airport) => airports.add(airport));
        }
        // 注意：如果contextAirports为空或未传递，表示用户没有选择机场，不应该传递机场筛选条件

        if (contextSupplier && contextSupplier.id) {
            supplierIds.add(contextSupplier.id);
        }
        // 注意：如果contextSupplier为空或未传递，表示用户没有选择供应商，不应该传递供应商筛选条件

        return {
            airports: Array.from(airports),
            supplierIds: Array.from(supplierIds),
        };
    }, [contextAirports, contextSupplier]);

    // 动态加载适用的邮件模板
    const loadAvailableTemplates = useCallback(async () => {
        if (!transactionType) return;

        try {
            setTemplateLoading(true);
            const contextInfo = getCurrentContextInfo();

            // 构建查询参数
            const queryParams = {
                category: transactionType, // 使用具体的事务类型
                status: "ACTIVE",
                page: 1,
                page_size: 50, // 获取足够多的模板
            };

            // 构建筛选参数 - 只有在有明确上下文时才传递筛选条件
            if (contextInfo.airports.length > 0) {
                // 有机场上下文时，传递机场筛选条件
                queryParams.airport = contextInfo.airports[0];
            }
            // 注意：不传递机场参数时，后端会返回所有机场的模板（包括通用模板）

            if (contextInfo.supplierIds.length > 0) {
                // 有供应商上下文时，传递供应商筛选条件
                queryParams.supplier_id = contextInfo.supplierIds[0];
            }
            // 注意：不传递供应商参数时，后端会返回所有供应商的模板（包括通用模板）

            // 发起查询
            const result = await emailTemplateApi.getTemplates(queryParams);
            setAvailableTemplates(result.data?.items || []);
        } catch (error) {
            console.error("加载可用模板失败:", error);
            message.error("加载邮件模板失败，请重试");
            setAvailableTemplates([]);
        } finally {
            setTemplateLoading(false);
        }
    }, [transactionType, getCurrentContextInfo]);

    // 根据可用模板生成模板选项
    const templateOptions = useMemo(() => {
        return availableTemplates.map((template, index) => ({
            code: template.template_code,
            title: template.name,
            description: template.description || `${template.name}模板`,
            icon: getTransactionIcon(transactionType),
            isDefault: index === 0, // 第一个作为默认选项
            template: template, // 保存完整的模板信息
        }));
    }, [availableTemplates, transactionType]);

    // 是否为批量发送
    const isBatchSending = selectedFlightIds.length > 1;

    // 加载选中航班的详细信息
    const loadSelectedFlightDetails = useCallback(() => {
        if (!selectedFlightIds || selectedFlightIds.length === 0) {
            setSelectedFlightDetails([]);
            setExtendedFlightData([]);
            return;
        }

        setLoading(true);
        try {
            const flightDetails = allFlightsData.filter((flight) => selectedFlightIds.includes(flight.id));
            setSelectedFlightDetails(flightDetails);

            // 异步加载扩展数据（包括前序航班）
            loadExtendedFlightData(flightDetails);
        } catch (error) {
            console.error("筛选航班详情失败:", error);
            message.error("加载航班数据失败");
            setSelectedFlightDetails([]);
            setExtendedFlightData([]);
        } finally {
            setLoading(false);
        }
    }, [selectedFlightIds, allFlightsData]);

    /**
     * 为每个航班查询前序航班（进港航班）
     *
     * ⚠️ 关键数据源函数：此函数为航班对象添加 predecessor 属性
     *
     * predecessor 的查询逻辑：
     * - 查找同一飞机在当前航班起飞前7天内，到达当前航班出发机场的最近航班
     * - 例如：CT107 从 HGH 起飞，则查找最近一个到达 HGH 的航班作为 predecessor
     *
     * 消费者：
     * - preprocessFlightData() → 为航食申请准备包含 predecessor 的数据
     * - FlightWithPredecessorRenderer → 渲染 arrival_flight_info 时读取 predecessor
     *
     * @param {Array} flightDetails - 原始航班数据
     * @returns {void} - 结果存储到 extendedFlightData 状态
     *
     * @see preprocessFlightData - 使用此数据的函数
     * @see FlightWithPredecessorRenderer.renderSingleFlightInline - 最终消费者
     */
    const loadExtendedFlightData = useCallback(async (flightDetails) => {
        if (!flightDetails || flightDetails.length === 0) {
            setExtendedFlightData([]);
            return;
        }

        try {
            // 为每个航班查询前序航班
            const flightsWithPredecessors = await Promise.all(
                flightDetails.map(async (flight) => {
                    try {
                        // 查询该飞机在该航班前7天内到达该机场的航班（按有效起飞时间 ETD > STD）
                        const effectiveDeparture = flight.etd || flight.std;
                        const flightDate = new Date(effectiveDeparture);
                        const startDate = new Date(flightDate);
                        startDate.setDate(startDate.getDate() - 7); // 前7天

                        const response = await flightOperationFlightService.getFlightOperationsWithTransactions({
                            aircraft_reg: flight.aircraft_reg,
                            arrival_airport: flight.departure_airport,
                            start_date: startDate.toISOString().split("T")[0],
                            end_date: flightDate.toISOString().split("T")[0],
                            status: "SCHEDULED,DELAYED,DEPARTED,COMPLETED,DIVERTED",
                            page_size: 10,
                        });

                        let predecessor = null;
                        if (response.data && response.data.items && response.data.items.length > 0) {
                            // 找到最近的前序航班（按有效到达 ETA > STA，有效起飞 ETD > STD）
                            const candidates = response.data.items.filter((f) => f.id !== flight.id && new Date(f.eta || f.sta || f.etd || f.std) < new Date(effectiveDeparture)).sort((a, b) => new Date(b.eta || b.sta || b.etd || b.std) - new Date(a.eta || a.sta || a.etd || a.std));

                            predecessor = candidates[0] || null;
                        }

                        return {
                            ...flight,
                            predecessor: predecessor,
                        };
                    } catch (error) {
                        console.error(`查询航班 ${flight.flight_number} 前序航班失败:`, error);
                        return {
                            ...flight,
                            predecessor: null,
                        };
                    }
                }),
            );

            setExtendedFlightData(flightsWithPredecessors);
        } catch (error) {
            console.error("加载前序航班数据失败:", error);
            // 失败时使用原始数据
            setExtendedFlightData(flightDetails.map((f) => ({ ...f, predecessor: null })));
        }
    }, []);

    // 批量加载所有模板详情
    const loadAllTemplates = useCallback(async () => {
        if (templateOptions.length === 0) return;

        try {
            const templateCodes = templateOptions.map((option) => option.code);

            // 并行加载所有模板的详细信息
            const templatePromises = templateCodes.map(async (code) => {
                try {
                    const response = await emailTemplateApi.getTemplateByCodeWithRecipients(code);
                    return { code, data: response.data };
                } catch (error) {
                    console.error(`加载模板 ${code} 失败:`, error);
                    return { code, data: null, error };
                }
            });

            const results = await Promise.all(templatePromises);

            // 构建模板映射
            const templatesMap = {};
            results.forEach(({ code, data }) => {
                if (data) {
                    templatesMap[code] = data;
                }
            });

            setAllTemplates(templatesMap);
        } catch (error) {
            console.error("批量加载模板失败:", error);
            message.error("加载邮件模板失败，请重试");
        }
    }, [templateOptions]);

    // 合并收件人：事务明细 + 模板默认收件人
    const collectAndMergeEmails = useCallback((templateRecipients = null, initialRecipients = { to: [], cc: [] }) => {
        const emailSet = new Set();
        const toEmails = [];
        const ccEmails = [];

        // 添加事务明细收件人（优先级最高）
        [...(initialRecipients.to || []), ...(initialRecipients.cc || [])].forEach((email, index) => {
            if (email && email.trim()) {
                const key = email.trim().toLowerCase();
                if (!emailSet.has(key)) {
                    emailSet.add(key);
                    const isFromCc = index >= (initialRecipients.to || []).length;
                    (isFromCc ? ccEmails : toEmails).push(email.trim());
                }
            }
        });

        // 追加模板默认收件人
        if (templateRecipients) {
            [...(templateRecipients.default_to || []), ...(templateRecipients.default_cc || [])].forEach((email, index) => {
                if (email && email.trim()) {
                    const key = email.trim().toLowerCase();
                    if (!emailSet.has(key)) {
                        emailSet.add(key);
                        const isFromCc = index >= (templateRecipients.default_to || []).length;
                        (isFromCc ? ccEmails : toEmails).push(email.trim());
                    }
                }
            });
        }

        return { toEmails, ccEmails, allEmailsInfo: [] };
    }, []);

    // 初始化默认模板选择
    const initializeDefaultTemplate = useCallback(() => {
        if (templateOptions.length > 0 && !isTemplateInitialized) {
            const defaultTemplate = templateOptions.find((option) => option.isDefault);
            if (defaultTemplate) {
                console.log("[UnifiedTransactionEmailDrawer] 初始化默认模板:", defaultTemplate.code);
                setSelectedTemplateCode(defaultTemplate.code);
                setIsTemplateInitialized(true);
            }
        }
    }, [templateOptions, isTemplateInitialized]);

    // 处理模板切换
    const handleTemplateChange = useCallback(
        (templateCode) => {
            console.log("[UnifiedTransactionEmailDrawer] 切换模板:", templateCode);

            if (selectedTemplateCode === templateCode) {
                return;
            }

            setSelectedTemplateCode(templateCode);

            // 立即更新选中的模板详情
            if (allTemplates[templateCode]) {
                setSelectedTemplate(allTemplates[templateCode]);
            } else {
                setSelectedTemplate(null);
            }
        },
        [allTemplates, selectedTemplateCode],
    );

    // 打开邮件编辑器
    const handleOpenEmailEditor = useCallback(async () => {
        if (!selectedTemplate) {
            message.warning("请先选择邮件模板");
            return;
        }

        setIsEmailEditorVisible(true);
    }, [selectedTemplate]);

    // 组件初始化
    useEffect(() => {
        if (visible && selectedFlightIds.length > 0 && transactionType) {
            loadSelectedFlightDetails();
        } else if (!visible) {
            // 抽屉关闭时清理数据
            setSelectedFlightDetails([]);
            setSelectedTemplateCode(null);
            setSelectedTemplate(null);
            setAllTemplates({});
            setIsTemplateInitialized(false);
            setAvailableTemplates([]);
        }
    }, [visible, selectedFlightIds.length, transactionType, loadSelectedFlightDetails]);

    // 加载预设发件账号
    useEffect(() => {
        if (visible) {
            getPresetSenderAccounts()
                .then((res) => {
                    if (res.success) {
                        setSenderOptions(res.data || []);
                    }
                })
                .catch((err) => {
                    console.error("加载预设发件账号失败:", err);
                });
        }
    }, [visible]);

    // 当航班详情加载完成后，加载可用模板
    useEffect(() => {
        if (visible && selectedFlightDetails.length > 0 && transactionType) {
            loadAvailableTemplates();
        }
    }, [visible, selectedFlightDetails.length, transactionType, loadAvailableTemplates]);

    // 当可用模板加载完成后，加载模板详情
    useEffect(() => {
        if (templateOptions.length > 0) {
            loadAllTemplates();
        }
    }, [templateOptions, loadAllTemplates]);

    // 模板初始化
    useEffect(() => {
        if (!templateLoading && templateOptions.length > 0 && Object.keys(allTemplates).length > 0) {
            initializeDefaultTemplate();
        }
    }, [templateLoading, templateOptions.length, allTemplates, initializeDefaultTemplate]);

    // 模板详情更新
    useEffect(() => {
        if (selectedTemplateCode && allTemplates[selectedTemplateCode] && isTemplateInitialized) {
            setSelectedTemplate(allTemplates[selectedTemplateCode]);
        }
    }, [selectedTemplateCode, allTemplates, isTemplateInitialized]);

    // 处理邮件发送
    const handleEmailSubmit = async (emailData) => {
        try {
            setIsSending(true);
            setSendingProgress(`正在发送${TRANSACTION_TYPE_LABELS[transactionType]}申请邮件...`);

            // 构建收件人列表
            const recipients = [];

            // 添加主收件人
            if (emailData.to && emailData.to.length > 0) {
                emailData.to.forEach((email) => {
                    if (email && email.trim()) {
                        recipients.push({
                            email: email.trim(),
                            type: "TO",
                        });
                    }
                });
            }

            // 添加抄送收件人
            if (emailData.cc && emailData.cc.length > 0) {
                emailData.cc.forEach((email) => {
                    if (email && email.trim()) {
                        recipients.push({
                            email: email.trim(),
                            type: "CC",
                        });
                    }
                });
            }

            // 添加密送收件人
            if (emailData.bcc && emailData.bcc.length > 0) {
                emailData.bcc.forEach((email) => {
                    if (email && email.trim()) {
                        recipients.push({
                            email: email.trim(),
                            type: "BCC",
                        });
                    }
                });
            }

            if (recipients.length === 0) {
                message.error("请至少添加一个收件人");
                return;
            }

            // 构建业务关联信息
            // 修正：record_type 应该反映实际存储的记录类型，而不是业务分类
            // linked_record_id 将存储航班ID，所以 record_type 应该是 "FLIGHT_OPERATION"
            // link_type 用于区分具体的业务场景
            const businessLink = {
                record_id: 0, // 批量邮件时此字段不使用，实际ID在 details.flight_operation_ids 中
                record_type: "FLIGHT_OPERATION", // 反映实际存储的记录类型
                link_type: `${transactionType}_APPLICATION_BATCH`, // 业务场景分类
                details: {
                    flight_operation_ids: selectedFlightIds,
                    transaction_type: transactionType,
                    transaction_label: TRANSACTION_TYPE_LABELS[transactionType],
                    batch_size: selectedFlightIds.length,
                    template_code: selectedTemplateCode,
                },
            };

            // 构建统一邮件API请求数据
            // 注意：后端 MailJobRequest 期望 is_bulk_split 和 request_read_receipt，不识别 bcc_sending
            const requestData = {
                business_link: businessLink,
                recipients: recipients,
                subject: emailData.subject || `${TRANSACTION_TYPE_LABELS[transactionType]}申请 - ${selectedFlightDetails.length}个航班`,
                body_html: emailData.contentHtml || "",
                body_text: emailData.contentText || null,
                attachments: emailData.attachments || [],
                sender_user_id: 1,
                user_email_config_id: null,
                // 使用后端期望的参数名（不展开 sendingOptions，避免传递无效字段）
                is_bulk_split: sendingOptions[SendingOptionType.BCC_SENDING] || false,
                request_read_receipt: sendingOptions[SendingOptionType.READ_RECEIPT] || false,
                send_interval: sendingOptions[SendingOptionType.BCC_SENDING] ? 1.0 : 0.1, // 群发单显时使用1秒间隔
                content_block_context: emailData.contentBlockContext || {
                    template_code: selectedTemplateCode,
                    instances: [],
                    business_context_snapshot: generateEmailContextData(),
                },
                preset_sender_key: emailData.selectedSender?.key || null, // 传递预设发件账号标识
            };

            // 调用统一邮件API
            const response = await sendUniversalEmail(requestData);

            if (response.success) {
                const useSplitSending = sendingOptions[SendingOptionType.BCC_SENDING];

                if (useSplitSending) {
                    // 异步发送模式 - 显示详细提示并引导查看发件箱
                    message.success({
                        content: (
                            <div>
                                <div style={{ fontWeight: "bold", marginBottom: 4 }}>邮件发送任务已启动</div>
                                <div style={{ fontSize: "12px", color: "#666" }}>{response.message || `${TRANSACTION_TYPE_LABELS[transactionType]}申请邮件正在后台发送给 ${recipients.length} 个收件人`}</div>
                                <div style={{ fontSize: "12px", color: "#1890ff", marginTop: 4 }}>由于开启了"群发单显"，系统将在后台逐一发送邮件</div>
                                <div style={{ fontSize: "12px", color: "#52c41a", marginTop: 4 }}>可前往「发件箱」查看发送进度和结果</div>
                            </div>
                        ),
                        duration: 8,
                    });
                } else {
                    // 同步发送模式 - 显示简洁的成功提示
                    message.success({
                        content: (
                            <div>
                                <div style={{ fontWeight: "bold", marginBottom: 4 }}>邮件发送成功</div>
                                <div style={{ fontSize: "12px", color: "#666" }}>{response.message || `${TRANSACTION_TYPE_LABELS[transactionType]}申请邮件已发送`}</div>
                            </div>
                        ),
                        duration: 4,
                    });
                }

                // 关闭邮件编辑器和抽屉
                setIsEmailEditorVisible(false);
                onClose();

                // 调用成功回调
                if (onSuccess && typeof onSuccess === "function") {
                    selectedFlightIds.forEach((flightId) => {
                        onSuccess(flightId);
                    });
                }
            } else {
                message.error(response.message || "邮件发送失败");
            }
        } catch (error) {
            console.error("发送邮件失败:", error);

            // 安全地提取错误消息
            let errorMessage = "发送邮件时发生错误";

            if (error.response?.data?.detail) {
                if (Array.isArray(error.response.data.detail)) {
                    const validationErrors = error.response.data.detail
                        .map((err) => {
                            const field = err.loc ? err.loc.join(".") : "未知字段";
                            return `${field}: ${err.msg}`;
                        })
                        .join("; ");
                    errorMessage = `数据验证失败: ${validationErrors}`;
                } else if (typeof error.response.data.detail === "string") {
                    errorMessage = error.response.data.detail;
                }
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }

            message.error(errorMessage);
        } finally {
            setIsSending(false);
            setSendingProgress(null);
        }
    };

    // 关闭邮件编辑器
    const handleEmailCancel = () => {
        setIsEmailEditorVisible(false);
        setSendingProgress(null);
    };

    /**
     * 预处理航班数据，根据事务类型准备不同格式的数据
     *
     * ╔══════════════════════════════════════════════════════════════════════════════╗
     * ║  ⚠️  数据契约警告 (Data Contract Warning)                                     ║
     * ╠══════════════════════════════════════════════════════════════════════════════╣
     * ║  对于 CATERING_ORDER (航食申请) 类型：                                        ║
     * ║  返回的 currentFlight 和 allFlights 必须使用 extendedFlightData              ║
     * ║  (不能使用 sortedFlights)，因为只有 extendedFlightData 包含 predecessor 属性 ║
     * ║                                                                              ║
     * ║  数据流：loadExtendedFlightData() → extendedFlightData → 此函数               ║
     * ║                                                                              ║
     * ║  消费者依赖：                                                                 ║
     * ║  - FlightWithPredecessorRenderer.renderSingleFlightInline()                  ║
     * ║    → 读取 currentFlight.predecessor 显示进港航班信息                          ║
     * ║  - FlightWithPredecessorRenderer.renderMultipleFlightsInline()               ║
     * ║    → 读取 allFlights[i].predecessor 显示进港航班列表                          ║
     * ║                                                                              ║
     * ║  如果使用错误的数据源，arrival_flight_info 内容块将显示 "-" 而非航班信息       ║
     * ╚══════════════════════════════════════════════════════════════════════════════╝
     *
     * @param {Array} flightDetails - 原始航班数据（不含 predecessor）
     * @returns {Object} 处理后的数据对象，结构因事务类型而异
     *
     * @see loadExtendedFlightData - predecessor 数据的来源
     * @see FlightWithPredecessorRenderer - 数据的消费者
     * @see ContentBlockNodeView - 调用渲染器的组件
     */
    const preprocessFlightData = useCallback(
        (flightDetails) => {
            if (!flightDetails || flightDetails.length === 0) return null;

            // 按有效起飞时间排序（ETD > STD）
            const sortedFlights = [...flightDetails].sort((a, b) => (new Date(a.etd || a.std)).getTime() - (new Date(b.etd || b.std)).getTime());

            // ⚠️ extendedFlightData 是异步加载的扩展数据，包含 predecessor 属性
            // 对于航食申请，必须使用此数据源
            const flightsToUse = extendedFlightData.length > 0 ? extendedFlightData : sortedFlights;

            // 根据事务类型返回不同的数据格式
            switch (transactionType) {
                case TRANSACTION_TYPES.FUEL_REQUEST:
                    // 燃油申请：使用简单的航班列表格式，不需要前序航班概念
                    return {
                        currentFlight: sortedFlights[0],
                        allFlights: sortedFlights,
                        flightOperations: sortedFlights,
                    };

                case TRANSACTION_TYPES.CATERING_ORDER:
                    // ╔═══════════════════════════════════════════════════════════╗
                    // ║  航食申请：必须使用 flightsToUse（包含 predecessor）        ║
                    // ║  不能使用 sortedFlights，否则进港航班信息无法显示            ║
                    // ╚═══════════════════════════════════════════════════════════╝
                    // 按有效起飞时间排序（ETD > STD）
                    const sortedFlightsToUse = [...flightsToUse].sort((a, b) => (new Date(a.etd || a.std)).getTime() - (new Date(b.etd || b.std)).getTime());
                    return {
                        // ⚠️ 关键：必须使用 sortedFlightsToUse[0]，它包含 predecessor 属性
                        currentFlight: sortedFlightsToUse[0],
                        allFlights: sortedFlightsToUse,
                    };

                default:
                    // 其他事务类型：使用简单格式
                    return {
                        currentFlight: sortedFlights[0],
                        allFlights: sortedFlights,
                        flightOperations: sortedFlights,
                    };
            }
        },
        [extendedFlightData, transactionType],
    );

    // 根据事务类型获取内容块配置
    const getContentBlockConfig = useCallback((transactionType) => {
        switch (transactionType) {
            case TRANSACTION_TYPES.FUEL_REQUEST:
                return {
                    blockType: "operationFlightList",
                    blocks: [
                        {
                            id: "operation_flight_list",
                            type: "operationFlightList",
                            title: "Flight Operations Schedule",
                            isInline: false, // 燃油模板主要使用表格形式
                        },
                    ],
                };
            case TRANSACTION_TYPES.CATERING_ORDER:
                return {
                    blockType: "flightWithPredecessor",
                    blocks: [
                        {
                            id: "departure_flight_info",
                            type: "flightWithPredecessor",
                            title: "出港航班信息",
                            isInline: true,
                        },
                        {
                            id: "departure_flight_datetime",
                            type: "flightWithPredecessor",
                            title: "出港航班日期时间",
                            isInline: true,
                        },
                        {
                            id: "arrival_flight_info",
                            type: "flightWithPredecessor",
                            title: "进港航班信息",
                            isInline: true,
                        },
                        {
                            id: "arrival_flight_datetime",
                            type: "flightWithPredecessor",
                            title: "进港航班日期时间",
                            isInline: true,
                        },
                        {
                            id: "flight_catering_table",
                            type: "flightWithPredecessor",
                            title: "Flight Catering Information",
                            isInline: false,
                        },
                    ],
                };
            default:
                // 其他事务类型默认使用 operationFlightList
                return {
                    blockType: "operationFlightList",
                    blocks: [
                        {
                            id: "operation_flight_list",
                            type: "operationFlightList",
                            title: "Flight Operations Schedule",
                            isInline: false,
                        },
                    ],
                };
        }
    }, []);

    // 生成邮件上下文数据
    const generateEmailContextData = useCallback(() => {
        // 预处理航班数据
        const processedFlightData = preprocessFlightData(selectedFlightDetails);

        // 获取当前事务类型的内容块配置
        const contentBlockConfig = getContentBlockConfig(transactionType);

        // 生成内容块数据
        const contentBlocks = contentBlockConfig.blocks.map((blockConfig) => {
            // 根据内容块类型决定传递的数据格式
            let blockData;
            if (blockConfig.type === "operationFlightList") {
                // operationFlightList 期望直接接收航班数组
                blockData = processedFlightData?.flightOperations || processedFlightData?.allFlights || selectedFlightDetails;
            } else {
                // 其他类型使用完整的处理后数据对象
                blockData = processedFlightData;
            }

            return {
                id: blockConfig.id,
                type: blockConfig.type,
                title: blockConfig.title,
                data: blockData,
                business_source: {
                    entity_type: "FlightOperation",
                    entity_ids: selectedFlightIds,
                },
                transaction_info: {
                    transaction_type: transactionType,
                    transaction_label: TRANSACTION_TYPE_LABELS[transactionType],
                    batch_size: selectedFlightIds.length,
                    template_code: selectedTemplateCode,
                },
            };
        });

        const contextData = {
            // V1模式兼容格式
            flightOperations: selectedFlightDetails,
            transactionType: transactionType,
            airportsMap,

            // V2模式需要的contentBlocks格式
            contentBlocks: contentBlocks,
        };

        console.log("[generateEmailContextData] 生成的上下文数据:", contextData);

        return contextData;
    }, [selectedFlightDetails, transactionType, selectedFlightIds, selectedTemplateCode, preprocessFlightData, getContentBlockConfig, airportsMap]);

    // 渲染选中的航班列表
    const renderSelectedFlights = () => {
        if (loading) {
            return (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                    <Spin size="large" />
                    <p style={{ marginTop: 16 }}>正在加载航班数据...</p>
                </div>
            );
        }

        if (selectedFlightDetails.length === 0) {
            return <Alert message="未选择航班" type="warning" showIcon />;
        }

        return (
            <div>
                <Alert
                    message={`即将发送${TRANSACTION_TYPE_LABELS[transactionType]}申请邮件的航班`}
                    description={
                        <div style={{ marginTop: "8px" }}>
                            <div>
                                <Text strong>批量{TRANSACTION_TYPE_LABELS[transactionType]}申请</Text>
                            </div>
                            <div style={{ color: "#666", fontSize: "12px", marginTop: "4px" }}>航班数量: {selectedFlightDetails.length}</div>
                        </div>
                    }
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            </div>
        );
    };

    // 渲染模板选择
    const renderTemplateSelection = () => {
        const contextInfo = getCurrentContextInfo();

        if (templateOptions.length === 0) {
            return (
                <Alert
                    message="暂无可用模板"
                    description={
                        <div>
                            <div>{`当前事务类型 "${TRANSACTION_TYPE_LABELS[transactionType]}" 暂无可用的邮件模板`}</div>
                            {contextInfo.airports.length > 0 && (
                                <div style={{ marginTop: 4, fontSize: "12px", color: "#666" }}>
                                    筛选条件：机场 {contextInfo.airports.join(", ")}
                                    {contextInfo.supplierIds.length > 0 && `, 供应商 ${contextSupplier?.name || contextInfo.supplierIds.join(", ")}`}
                                </div>
                            )}
                        </div>
                    }
                    type="warning"
                    showIcon
                />
            );
        }

        return (
            <div>
                {/* 显示筛选信息 */}
                {contextInfo.airports.length > 0 && (
                    <div style={{ marginBottom: 12, padding: "8px 12px", backgroundColor: "#f6ffed", border: "1px solid #b7eb8f", borderRadius: "4px", fontSize: "12px" }}>
                        <span style={{ color: "#52c41a", fontWeight: "bold" }}>智能筛选：</span>
                        <span style={{ color: "#666" }}>
                            已根据机场 <strong>{contextInfo.airports.join(", ")}</strong>
                            {contextInfo.supplierIds.length > 0 && (
                                <span>
                                    {" "}
                                    和供应商 <strong>{contextSupplier?.name || contextInfo.supplierIds.join(", ")}</strong>
                                </span>
                            )}{" "}
                            筛选出 {templateOptions.length} 个适用模板
                        </span>
                    </div>
                )}

                <Row gutter={[12, 12]}>
                    {templateOptions.map((template) => (
                        <Col span={templateOptions.length > 2 ? 12 : 24} key={template.code}>
                            <Card
                                size="small"
                                hoverable
                                style={{
                                    cursor: "pointer",
                                    minHeight: "6em",
                                    border: selectedTemplateCode === template.code ? "2px solid #1890ff" : "1px solid #d9d9d9",
                                    backgroundColor: selectedTemplateCode === template.code ? "#f0f8ff" : "#fff",
                                }}
                                onClick={() => handleTemplateChange(template.code)}
                                styles={{ body: { padding: 12 } }}
                            >
                                <div style={{ display: "flex", alignItems: "flex-start" }}>
                                    <div style={{ fontSize: 20, marginRight: 8, marginTop: 2 }}>{template.icon}</div>
                                    <div style={{ flex: 1 }}>
                                        <div
                                            style={{
                                                fontWeight: selectedTemplateCode === template.code ? "bold" : "normal",
                                                color: selectedTemplateCode === template.code ? "#1890ff" : "#000",
                                                fontSize: 14,
                                                marginBottom: 4,
                                            }}
                                        >
                                            {template.title}
                                        </div>
                                        <div
                                            style={{
                                                fontSize: 12,
                                                color: "#666",
                                                lineHeight: "1.4",
                                            }}
                                        >
                                            {template.description}
                                        </div>
                                    </div>
                                </div>
                                {selectedTemplateCode === template.code && (
                                    <div
                                        style={{
                                            position: "absolute",
                                            top: 8,
                                            right: 8,
                                            color: "#1890ff",
                                            fontSize: 16,
                                        }}
                                    >
                                        ✓
                                    </div>
                                )}
                            </Card>
                        </Col>
                    ))}
                </Row>
            </div>
        );
    };

    const drawerTitle = useMemo(() => {
        const typeText = TRANSACTION_TYPE_LABELS[transactionType] || "事务";
        const countText = isBatchSending ? `批量发送${typeText}申请邮件 (${selectedFlightIds.length}个航班)` : `发送${typeText}申请邮件`;
        return countText;
    }, [transactionType, isBatchSending, selectedFlightIds.length]);

    return (
        <>
            <Drawer title={drawerTitle} placement="right" width="50%" onClose={onClose} open={visible} destroyOnHidden={true}>
                <div className="unified-transaction-email-content">
                    {/* 选中的航班 */}
                    <div style={{ marginBottom: 20 }}>
                        <Title level={5} style={{ marginBottom: 12, color: "#1890ff" }}>
                            选中的航班
                        </Title>
                        {renderSelectedFlights()}
                    </div>

                    <Divider style={{ margin: "16px 0" }} />

                    {/* 模板选择 */}
                    <div style={{ marginBottom: 20 }}>
                        <Title level={5} style={{ marginBottom: 12, color: "#1890ff" }}>
                            邮件模板
                        </Title>
                        {templateLoading ? (
                            <div style={{ textAlign: "center", padding: "40px 0" }}>
                                <Spin size="large" />
                                <div style={{ marginTop: 12, color: "#666" }}>正在加载模板内容...</div>
                            </div>
                        ) : (
                            renderTemplateSelection()
                        )}
                    </div>

                    <Divider style={{ margin: "16px 0" }} />

                    {/* 操作按钮 */}
                    <div style={{ textAlign: "center", paddingTop: 16, borderTop: "1px solid #f0f0f0" }}>
                        <Space size="large">
                            <Button onClick={onClose} disabled={isSending}>
                                取消
                            </Button>
                            <Button type="primary" icon={<SendOutlined />} onClick={handleOpenEmailEditor} loading={isEmailLoading || isSending} disabled={!selectedTemplateCode || selectedFlightDetails.length === 0 || isSending}>
                                {isSending ? "正在发送..." : "打开邮件编辑器"}
                            </Button>
                        </Space>
                        {/* 发送进度显示 */}
                        {sendingProgress && (
                            <div style={{ marginTop: 12, color: "#1890ff", fontSize: "12px" }}>
                                <Spin size="small" style={{ marginRight: 8 }} />
                                {sendingProgress}
                            </div>
                        )}
                    </div>
                </div>
            </Drawer>

            {/* 邮件编辑器 */}
            {isEmailEditorVisible &&
                selectedTemplate &&
                (() => {
                    // 合并收件人：先用事务明细初始收件人，再附加模板默认收件人
                    const templateRecipients = {
                        default_to: selectedTemplate?.resolved_to || [],
                        default_cc: selectedTemplate?.resolved_cc || [],
                    };
                    const mergedEmails = collectAndMergeEmails(templateRecipients, { to: initialToRecipients, cc: initialCcRecipients });

                    // 传给表单以便回填分组（仅当表单空时才回填，不会覆盖已设置的）
                    const templateRecipientsForForm = {
                        default_to: selectedTemplate?.default_to || null,
                        default_cc: selectedTemplate?.default_cc || null,
                    };

                    return (
                        <GeneralEmailEditor
                            visible={isEmailEditorVisible}
                            emailType={EmailType.GENERAL}
                            sendingOptions={sendingOptions}
                            onSendingOptionsChange={setSendingOptions}
                            senderOptions={senderOptions}
                            defaultSender={adaptUserEmailConfig(userEmailConfig)}
                            senderEditable={true}
                            onSubmit={handleEmailSubmit}
                            onCancel={handleEmailCancel}
                            title={`发送${TRANSACTION_TYPE_LABELS[transactionType]}申请邮件${mergedEmails.allEmailsInfo.length > 0 ? ` (已自动添加${mergedEmails.allEmailsInfo.length}个联系邮箱)` : ""}`}
                            subjectTemplate={selectedTemplate?.subject_template || `${TRANSACTION_TYPE_LABELS[transactionType]}申请`}
                            templateContent={selectedTemplate?.body_html || ""}
                            templateRecipients={templateRecipientsForForm}
                            to={mergedEmails.toEmails}
                            cc={mergedEmails.ccEmails}
                            businessContext={generateEmailContextData()}
                            enableV2Mode={true}
                            loading={isSending}
                            loadingText={sendingProgress}
                            cancelDisabled={isSending}
                        />
                    );
                })()}
        </>
    );
};

export default UnifiedTransactionEmailDrawer;
